/**
 * Onboarding flow screens, triggers, use cases, and progress tracking.
 */

import type { LucideIcon } from "lucide-react";
import { Mail } from "lucide-react";
import { HouseWifi } from "@/components/animate-ui/icons/house-wifi";
import { PlugZap } from "@/components/animate-ui/icons/plug-zap";
import { RotateCcw } from "@/components/animate-ui/icons/rotate-ccw";
import { ChecklistScreen } from "@/components/onboarding/screens/checklist";
import { IntegrationsScreen } from "@/components/onboarding/screens/integrations";
import { TaskCreationScreen } from "@/components/onboarding/screens/task-creation";
import { TriggersScreen } from "@/components/onboarding/screens/triggers";
import { WelcomeScreen } from "@/components/onboarding/screens/welcome";
import { INTEGRATIONS, type IntegrationRef } from "./integration-logos";

/**
 * Onboarding screen definition
 */
export interface OnboardingScreen {
	title: string;
	description: string;
	component: React.ComponentType;
}

/**
 * Base onboarding screens (always shown)
 */
export const BASE_ONBOARDING_SCREENS: OnboardingScreen[] = [
	{
		title: "Welcome to m8tes!",
		description: "Tasks that run themselves. Reports, research, and real actions.",
		component: WelcomeScreen,
	},
	{
		title: "Describe Your Tasks",
		description: "Delegate tasks in plain English",
		component: TaskCreationScreen,
	},
	{
		title: "Connect Your Apps",
		description: "Connect your apps. Pull data. Take action.",
		component: IntegrationsScreen,
	},
	{
		title: "Choose How Tasks Run",
		description: "Choose when work gets done",
		component: TriggersScreen,
	},
];

/**
 * Checklist screen (shown when all checklist items are completed)
 */
export const CHECKLIST_ONBOARDING_SCREEN: OnboardingScreen = {
	title: "Getting Started Checklist",
	description: "Complete these steps to make the most of m8tes",
	component: ChecklistScreen,
};

/**
 * Trigger type for onboarding
 */
export interface OnboardingTrigger {
	icon: LucideIcon | React.ComponentType<{ className?: string; animateOnHover?: boolean }>;
	title: string;
	description: string;
}

/**
 * Task trigger types shown in onboarding
 */
export const ONBOARDING_TRIGGERS: OnboardingTrigger[] = [
	{
		icon: PlugZap,
		title: "On Demand",
		description: "Run tasks and ask questions whenever you need to.",
	},
	{
		icon: RotateCcw,
		title: "On a Schedule",
		description: "Automate recurring work — daily, weekly, or monthly.",
	},
	{
		icon: Mail,
		title: "Send an Email",
		description: "Email your teammate to kick off a task or ask a question.",
	},
	{
		icon: HouseWifi,
		title: "Events from Apps",
		description: "Trigger tasks automatically when something happens in your apps.",
	},
];

/**
 * Use case item for welcome screen.
 * Shows integration logos instead of generic icons.
 */
export interface OnboardingUseCase {
	integrations: IntegrationRef[];
	title: string;
}

/**
 * Perfect for items shown in welcome screen
 */
export const ONBOARDING_USE_CASES: OnboardingUseCase[] = [
	{
		integrations: [INTEGRATIONS["google-ads"]],
		title: "Check Google Ads for wasted spend and add negative keywords",
	},
	{
		integrations: [INTEGRATIONS.hubspot],
		title: "Find stalled deals in HubSpot and draft follow-up emails",
	},
	{
		integrations: [INTEGRATIONS.stripe],
		title: "Pull Stripe metrics every Monday, update Sheets, and alert Slack if churn spikes",
	},
];

/**
 * Checklist item for onboarding progress tracking
 */
export interface ChecklistItem {
	id: string;
	label: string;
	completed: boolean;
}

/**
 * Default checklist items for new users
 */
export const DEFAULT_CHECKLIST: ChecklistItem[] = [
	{
		id: "automate_task",
		label: "Run first task",
		completed: false,
	},
	{
		id: "connect_integration",
		label: "Connect apps",
		completed: false,
	},
	{
		id: "create_automation",
		label: "Create an automation",
		completed: false,
	},
];
